import { renderCpfNotVerifiedMessage, renderSSOErrorMessage } from './errorMessageRenderers'

const SHARED_MESSAGES = {
  email: {
    validation: 'Insira um endereço de e-mail válido para continuar',
    serverInvalid: 'É necessário um endereço de e-mail válido',
  },
  name: {
    empty: 'Digite seu nome',
    invalid: 'Insira seu nome e sobrenome',
  },
  tooManyAttempts: {
    login:
      'Você excedeu o número de tentativas permitido. Clique em "Esqueceu a senha?" ou tente novamente mais tarde.',
    recovery:
      'Você excedeu o número de tentativas para a recuperação de senha. Tente novamente mais tarde.',
  },
  signup: {
    generic: 'Não foi possível criar a sua conta. Tente novamente.',
  },
} as const

export const ERROR_MESSAGES = {
  generic: {
    default: 'Desculpe, algo deu errado. Tente novamente.',
    network: 'Desculpe, não conseguimos. Tente novamente.',
  },

  login: {
    email: {
      validation: {
        empty: SHARED_MESSAGES.email.validation,
        invalid: SHARED_MESSAGES.email.validation,
      },
      server: {
        invalid: SHARED_MESSAGES.email.serverInvalid,
        accountExists:
          'Você já possui uma conta. Acesse com esse e-mail ou cadastre-se com outro e-mail.',
        tooManyAttempts: SHARED_MESSAGES.tooManyAttempts.login,
        b2bLimitReached:
          'O limite de acessos de sua organização foi atingido. Em caso de dúvidas, contate o administrador.',
        emailValidationFailed:
          'Não foi possível validar esse e-mail. Por favor, verifique se o e-mail está correto. Caso o erro persista, entre em contato com seu provedor de e-mail.',
        emailValidationError:
          'Não foi possível validar esse e-mail. Por favor, tente novamente mais tarde.',
      },
    },
    password: {
      validation: {
        empty: 'Insira sua senha',
      },
      server: {
        required: 'E-mail e senha são obrigatórios.',
        tooManyAttempts: SHARED_MESSAGES.tooManyAttempts.login,
        incorrect: 'E-mail ou senha incorretos. Verifique as informações e tente novamente.',
        userBlocked: 'Este usuário encontra-se bloqueado no momento.',
        incorrectFallback: 'Senha incorreta',
      },
    },
  },

  signup: {
    email: {
      validation: {
        empty: SHARED_MESSAGES.email.validation,
        invalid: SHARED_MESSAGES.email.validation,
      },
    },
    details: {
      name: {
        empty: SHARED_MESSAGES.name.empty,
        invalid: SHARED_MESSAGES.name.invalid,
      },
      occupation: {
        empty: 'Você deve informar sua ocupação',
      },
      password: {
        empty: 'Insira sua nova senha',
      },
      server: {
        allFieldsRequired: 'Todos os campos são necessários.',
        nameInvalid: `${SHARED_MESSAGES.name.invalid}.`,
        occupationInvalid: 'Ocupação inválida.',
        passwordTooShort: (minLength: number) =>
          `A senha deve conter no mínimo ${minLength} caracteres.`,
        generic: SHARED_MESSAGES.signup.generic,
      },
    },
    otp: {
      server: {
        invalid: 'Código inválido ou expirado',
        generic: SHARED_MESSAGES.signup.generic,
      },
    },
    b2b: {
      name: {
        empty: SHARED_MESSAGES.name.empty,
        invalid: SHARED_MESSAGES.name.invalid,
      },
      server: {
        generic: SHARED_MESSAGES.signup.generic,
      },
    },
    cpf: {
      tokenNotProvided: 'Token de cadastro não fornecido',
      cpf: {
        empty: 'Digite seu CPF',
        invalid: 'CPF inválido',
      },
      birthDate: {
        empty: 'Digite sua data de nascimento',
        invalid: 'Data inválida',
        belowMinimumAge: {
          title: 'Idade não permitida.',
          description: 'Para criar uma conta no Jusbrasil, você precisa ter pelo menos 16 anos.',
        },
      },
      phone: {
        invalid: 'Telefone inválido. Informe 10 ou 11 dígitos.',
      },
      server: {
        generic: SHARED_MESSAGES.signup.generic,
      },
    },
    cpfNotVerified: {
      title: 'Identidade não pôde ser verificada',
      message: renderCpfNotVerifiedMessage,
    },
    attempts: {
      title: 'Limite de contas excedido',
      message: 'Não foi possível concluir este cadastro.',
    },
  },

  changePassword: {
    currentPassword: {
      empty: 'Insira sua senha atual',
    },
    newPassword: {
      tooShort: (minLength: number) => `A senha deve conter ao menos ${minLength} caracteres`,
      sameAsCurrent: 'A nova senha deve ser diferente da senha atual.',
    },
    confirmPassword: {
      mismatch: 'As senhas não conferem. Tente novamente.',
    },
    server: {
      incorrectCurrent: 'A senha não confere. Tente novamente.',
      generic: 'Ocorreu um erro ao tentar alterar sua senha.',
    },
  },

  changeEmail: {
    email: {
      empty: 'Campo obrigatório',
      invalid: 'E-mail inválido',
      sameAsCurrent: 'O novo e-mail deve ser diferente do atual.',
    },
    confirmEmail: {
      mismatch: 'Os e-mails não conferem. Tente novamente.',
    },
    server: {
      blockedByPartnership:
        'Você não está autorizado a alterar seu e-mail, pois tem vínculo com uma parceria ativa.',
      generic: 'Ocorreu um erro ao tentar solicitar a alteração de e-mail.',
    },
  },

  passwordRecovery: {
    email: {
      validation: {
        empty: SHARED_MESSAGES.email.validation,
        invalid: SHARED_MESSAGES.email.validation,
      },
      server: {
        invalid: `${SHARED_MESSAGES.email.serverInvalid}.`,
        csrf: 'Recarregue a página e tente novamente.',
        tooManyAttempts: SHARED_MESSAGES.tooManyAttempts.recovery,
      },
    },
    reset: {
      password: {
        empty: 'Insira sua nova senha',
      },
      confirmPassword: {
        empty: 'Confirme sua nova senha',
        mismatch: 'As senhas não conferem. Tente novamente',
      },
      server: {
        generic: 'Ocorreu um erro ao alterar sua senha. Por favor, tente novamente mais tarde.',
      },
    },
  },

  sso: {
    error: {
      title: 'Não foi possível integrar seu login ao Jusbrasil',
      message: renderSSOErrorMessage,
    },
  },
} as const

export const REASON_MESSAGES = {
  passwordChanged: 'Sua senha foi alterada recentemente. Por favor, acesse novamente.',
  adminLoginWithoutGoogle:
    'Administrador só pode fazer login usando Google com e-mail @jusbrasil.com.br, clique no botão "Entre com Google" para isso.',
  adminRemovedPrivileges:
    'Não foi identificado e-mail do Jusbrasil, privilégios de administrador foram removidos, tente fazer login novamente.',
} as const

export type ReasonKey = keyof typeof REASON_MESSAGES

function isReasonKey(reason: string): reason is ReasonKey {
  return reason in REASON_MESSAGES
}

export function getReasonMessage(reason: string): string | undefined {
  if (isReasonKey(reason)) {
    return REASON_MESSAGES[reason]
  }
  return undefined
}
